import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api, User, UserProfile } from '../lib/api';
import { getDomainLimit, canConfigurePagePath, canExportReport } from '../../shared/tiers';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  isPremium: boolean;
  isLoading: boolean;
  isFree: boolean;
  hasFeatureAccess: (feature: string) => boolean;
  canAnalyze: () => { allowed: boolean; reason?: string };
  domainLimit: number;
  canConfigurePagePath: boolean;
  canExportReport: boolean;
  refreshProfile: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = api.getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const userProfile = await api.getProfile();
      setUser({ id: userProfile.id, email: userProfile.email });
      setProfile(userProfile);
    } catch (error) {
      console.error('Auth check failed:', error);
      api.signout();
      setUser(null);
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const result = await api.signup(email, password);
      setUser(result.user);
      await refreshProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const result = await api.signin(email, password);
      setUser(result.user);
      await refreshProfile();
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    api.signout();
    setUser(null);
    setProfile(null);
  };

  const refreshProfile = async () => {
    if (api.getToken()) {
      try {
        const userProfile = await api.getProfile();
        setProfile(userProfile);
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    }
  };

  const isPremium = profile?.subscriptionTier === 'premium' || profile?.subscriptionTier === 'business' || profile?.subscriptionTier === 'enterprise';
  const isFree = profile?.subscriptionTier === 'free' || !profile;

  const hasFeatureAccess = (feature: string): boolean => {
    if (!user) return false;

    const tier = profile?.subscriptionTier || 'free';
    const tierHierarchy = ['free', 'starter', 'professional', 'business', 'enterprise'];
    const currentTierIndex = tierHierarchy.indexOf(tier);

    const featureRequirements: { [key: string]: number } = {
      'basic_analysis': 0,
      'visibility_score': 1,
      'performance_metrics': 2,
      'ai_chat': 2,
      'advanced_analytics': 3,
      'api_access': 3,
      'white_label': 4,
    };

    const requiredTierIndex = featureRequirements[feature] ?? 0;
    return currentTierIndex >= requiredTierIndex;
  };

  const canAnalyze = (): { allowed: boolean; reason?: string } => {
    if (!user) {
      return { allowed: false, reason: 'Please sign in to analyze websites' };
    }

    if (!profile) {
      return { allowed: false, reason: 'Loading user data...' };
    }

    const analysisLimit = 3; // Default free tier limit
    const analysisCount = profile.analysisCount || 0;

    if (profile.isPremium) {
      return { allowed: true }; // Premium users have unlimited analyses
    }

    if (analysisCount >= analysisLimit) {
      return {
        allowed: false,
        reason: `You've reached your monthly limit of ${analysisLimit} analyses. Upgrade to analyze more websites.`,
      };
    }

    return { allowed: true };
  };

  const tier = profile?.subscriptionTier || 'free';
  const domainLimit = getDomainLimit(tier);
  const canPagePath = canConfigurePagePath(tier);
  const canExport = canExportReport(tier);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        isPremium,
        isLoading,
        isFree,
        hasFeatureAccess,
        canAnalyze,
        domainLimit,
        canConfigurePagePath: canPagePath,
        canExportReport: canExport,
        refreshProfile,
        signUp,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
